import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, overlayStyle, modalStyle, FormRow } from './AcademyShared';

const STATUSES = ['active', 'inactive', 'archived'];

export default function AcademyCategories() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [companyCode, setCompanyCode] = useState('');
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/academy/categories?per_page=50`;
      if (status) url += `&status=${status}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status, search]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => {
    fetch(`${API}/companies/${companyId}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : {})
      .then(data => setCompanyCode(String(data.company?.code || data.code || '').trim()))
      .catch(() => setCompanyCode(''));
  }, [companyId]);

  const changeStatus = async (id, newStatus) => {
    await fetch(`${API}/companies/${companyId}/academy/categories/${id}/status`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus }),
    });
    fetchList();
  };

  const removeItem = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    const res = await fetch(`${API}/companies/${companyId}/academy/categories/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Cannot delete'); return; }
    fetchList();
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>📂 Categories</h2>
        <button style={btnPrimary} onClick={() => setEditing({})}>+ New Category</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <input style={{ ...inputStyle, width: 220 }} placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={{ ...inputStyle, width: 160 }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Code', 'Name', 'Bootcamp Types', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No categories</td></tr>}
              {list.map(c => (
                <tr key={c.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{c.category_code}</td>
                  <td style={tdStyle}>{c.name}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>{c.bootcamp_types_count ?? 0}</td>
                  <td style={tdStyle}><Badge value={c.status} /></td>
                  <td style={tdStyle}>
                    <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => setEditing(c)}>Edit</button>
                    {c.status === 'active' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => changeStatus(c.id, 'inactive')}>Deactivate</button>}
                    {c.status === 'inactive' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => changeStatus(c.id, 'active')}>Activate</button>}
                    {c.status !== 'archived' && <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => changeStatus(c.id, 'archived')}>Archive</button>}
                    <button style={{ ...btnSmall, color: '#ef4444' }} onClick={() => removeItem(c.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && <CategoryModal companyId={companyId} companyCode={companyCode} category={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchList(); }} />}
    </div>
  );
}

function CategoryModal({ companyId, companyCode, category, onClose, onSaved }) {
  const isNew = !category.id;
  const [form, setForm] = useState({
    name: category.name || '', description: category.description || '', status: category.status || 'active',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { alert('Name is required'); return; }
    if (isNew && !form.code_suffix?.trim()) { alert('Enter the category code suffix.'); return; }
    setSaving(true);
    try {
      const url = isNew ? `${API}/companies/${companyId}/academy/categories` : `${API}/companies/${companyId}/academy/categories/${category.id}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
      onSaved();
    } catch (e) { console.error(e); setSaving(false); }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{isNew ? 'New Category' : 'Edit Category'}</h3>
        <FormRow label="Name *">
          <input style={inputStyle} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        </FormRow>
        <FormRow label="Category Code *">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>{companyCode ? `${companyCode}-CAT-` : 'Company code is loading…'}</span>
            <input aria-label="Code suffix" style={{ ...inputStyle, flex: '1 1 140px' }} value={form.code_suffix || ''} onChange={e => setForm({ ...form, code_suffix: e.target.value })} placeholder={isNew ? 'Enter the last part, e.g. TECH' : 'Leave blank to keep existing code'} />
          </div>
          {!isNew && <small>Current code: {category.category_code}</small>}
        </FormRow>
        <FormRow label="Description">
          <textarea style={{ ...inputStyle, minHeight: 70 }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
        </FormRow>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
